/* cspell:ignore nics */
import get from 'lodash/get';

import { commaTextToStartupArray } from '@/components-biz/ImageSelect';
import { hasMeaningfulSharedMemoryConfig, readSharedMemoryFromFormValues } from '@/delivery-type-plugin/capabilities/shared-memory';

import { readConfiguredCpuFromRecord } from '../../../capabilities/compute';
import { type VolumeItem, isVolumeClaimTemplateItem, normalizeContainerVolumeList } from '../../../capabilities/container-storage/utils';
import { readRegistryPayloadFromValues } from '../../../capabilities/image';
import { deserializeServiceListFromApi } from '../../../capabilities/service/serviceApiField';
import { type StartupArgFormRow, startupArgFormRowsToApiTokens } from '../../../capabilities/startup-command-args/utils';
import { normalizeVendorDeviceMeta, readVendorDeviceMeta } from '../../../components/vendorDevice';
import type { PipelineContext } from '../../../core/types';
import { buildSystemOptionsFromForm } from '../../../platform/container-deploy-config';
import { buildDefaultDirectResourceModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { hasMeaningfulValue } from '../../../utils';
import { readFormArchitecture } from '../../_shared/common/formHelpers';
import { resolveEnabledModulesForSpecFilter } from '../../_shared/common/sku/resolveEnabledModulesForSpecFilter';
import { normalizeGpuFromApi, normalizeMemoryForForm } from './apiFormNormalize';
import { flattenDnsNameservers, flattenDnsSearches, toFormDnsNameservers, toFormDnsSearches } from './dnsForm';
import { filterCiSpecByEnabledModules } from './enabledModules';
import { readCiGpuFromForm } from './gpuForm';
import type { CiSpec } from './schema';

function readFormNetworkName(value: string | undefined): string | undefined {
  if (!hasMeaningfulValue(value)) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function readFormNetworkRef(ref?: { name?: string } | string): { name: string } | undefined {
  const rawName = typeof ref === 'string' ? ref : ref?.name;
  const name = readFormNetworkName(rawName);
  return name ? { name } : undefined;
}

const DEFAULT_CONTAINER_SERVICES = [
  {
    name: 'ssh',
    usageScenario: 'SSH',
    protocol: 'TCP',
    internalPort: 22,
    externalPort: 0,
    description: 'SSH forwarding',
    _isDefault: true,
  },
  {
    name: 'vscode',
    usageScenario: 'HTTP',
    protocol: 'TCP',
    internalPort: 8080,
    externalPort: 0,
    description: 'Visual Studio Code Server',
    _isDefault: true,
  },
];

function resolveSharedMemoryDevices(
  values: Record<string, unknown>,
  formDevices?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const devices = { ...(formDevices ?? {}) };
  const sharedMemoryEnabled = values._sharedMemoryEnabled !== false;
  const sharedMemory = sharedMemoryEnabled ? readSharedMemoryFromFormValues(values) : undefined;
  if (sharedMemoryEnabled && hasMeaningfulSharedMemoryConfig(sharedMemory) && sharedMemory?.sizeLimit) {
    devices.sharedMemory = { size: String(sharedMemory.sizeLimit).trim() };
  } else {
    delete devices.sharedMemory;
  }
  return Object.keys(devices).length ? devices : undefined;
}

function readImageSystem(values: Record<string, unknown>): CiSpec['system'] {
  const image = get(values, 'basic.image', {}) as Record<string, unknown>;
  const formSystem = get(values, 'system', {}) as Record<string, unknown>;
  const commandText = image.command ?? formSystem.command;
  const argsRows = image.startupArgs;
  const argsFromRows =
    Array.isArray(argsRows) && argsRows.length
      ? startupArgFormRowsToApiTokens(argsRows as StartupArgFormRow[])
      : commaTextToStartupArray(image.args ?? formSystem.args);
  const envRaw = Array.isArray(image.env) ? image.env : formSystem.env;
  const systemOptions = buildSystemOptionsFromForm(values);
  const formDevices = formSystem.devices as Record<string, unknown> | undefined;
  const devices = resolveSharedMemoryDevices(values, formDevices);

  return {
    url: String(image.url ?? image.image ?? formSystem.url ?? '').trim() || undefined,
    image: String(image.image ?? image.url ?? formSystem.image ?? '').trim() || undefined,
    imageID: String(image.key ?? image.imageKey ?? formSystem.imageID ?? '').trim() || undefined,
    name: String(image.name ?? image.imageName ?? formSystem.name ?? '').trim() || undefined,
    architecture: readFormArchitecture(values),
    os: String(image.os ?? formSystem.os ?? '').trim() || undefined,
    version: String(image.version ?? formSystem.version ?? '').trim() || undefined,
    command: commaTextToStartupArray(commandText),
    args: argsFromRows.length ? argsFromRows : undefined,
    env: Array.isArray(envRaw)
      ? envRaw
          .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const row = item as { name?: string; key?: string; value?: unknown };
            const name = String(row.name ?? row.key ?? '').trim();
            if (!name) return null;
            return { name, value: row.value != null ? String(row.value) : '' };
          })
          .filter((item): item is { name: string; value: string } => Boolean(item))
      : undefined,
    security: formSystem.security as Record<string, unknown> | undefined,
    devices,
    ...(systemOptions ? { options: systemOptions } : {}),
  };
}

function readMemoryGi(raw: unknown): number | undefined {
  if (!hasMeaningfulValue(raw)) return undefined;
  const text = String(raw).trim().replace(/Gi$/i, '');
  const num = Number(text);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function readCpuRequest(values: Record<string, unknown>): number | undefined {
  return readConfiguredCpuFromRecord(get(values, 'resource.cpu'));
}

function readGpu(raw: unknown): CiSpec['resource']['gpu'] {
  return readCiGpuFromForm(raw);
}

function readSystemDiskSize(values: Record<string, unknown>): number | undefined {
  const raw = values.systemDiskSize;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function enrichVolumesFromForm(values: Record<string, unknown>, volumes: VolumeItem[]): VolumeItem[] {
  return normalizeContainerVolumeList(volumes).map((item, index) => {
    if (!isVolumeClaimTemplateItem(item)) return item;
    const pricing = normalizeVendorDeviceMeta(readVendorDeviceMeta(values, `volumes.${index}.pricing`) ?? item.pricing);
    return pricing ? { ...item, pricing } : item;
  });
}

export function ciFromForm(values: Record<string, unknown>, ctx: PipelineContext): CiSpec {
  const network = get(values, 'resource.network', {}) as Record<string, unknown>;
  const nicEnabled = values._nicEnabled !== false;
  const rawNics = get(values, 'network.nics') as CiSpec['resource']['network']['nics'];
  const nics = nicEnabled && Array.isArray(rawNics) && rawNics.length ? rawNics : undefined;
  const dnsConfig = get(network, 'dnsConfig', {}) as Record<string, unknown>;
  const services = get(network, 'services', get(network, 'service')) as CiSpec['resource']['network']['services'];
  const optionMeta = get(values, 'optionMeta');

  const spec: CiSpec = {
    name: String(values.name ?? ''),
    description: values.description ? String(values.description) : undefined,
    runStrategy: String(values.runStrategy ?? 'Always'),
    workspace: values.workspace ? String(values.workspace) : undefined,
    namespace: values.namespace ? String(values.namespace) : ctx.namespace,
    datacenter: values.datacenter ? String(values.datacenter) : ctx.overlay?.datacenter,
    creator: ctx.overlay?.creator,
    system: readImageSystem(values),
    systemDiskSize: readSystemDiskSize(values),
    systemDiskPricing: normalizeVendorDeviceMeta(readVendorDeviceMeta(values, 'systemDiskPricing')),
    resource: {
      cpu: {
        physicalCards: Number(get(values, 'resource.cpu.physicalCards')) || undefined,
        physicalCores: Number(get(values, 'resource.cpu.physicalCores')) || undefined,
        logicalCores: Number(get(values, 'resource.cpu.logicalCores')) || undefined,
        request: readCpuRequest(values),
        limit: readCpuRequest(values),
        pricing: normalizeVendorDeviceMeta(readVendorDeviceMeta(values, 'resource.cpu.pricing')),
      },
      memory: readMemoryGi(get(values, 'resource.memory')),
      memoryPricing: normalizeVendorDeviceMeta(readVendorDeviceMeta(values, 'resource.memoryPricing')),
      cpuPricing: normalizeVendorDeviceMeta(readVendorDeviceMeta(values, 'resource.cpuPricing')),
      gpuPricing: normalizeVendorDeviceMeta(readVendorDeviceMeta(values, 'resource.gpuPricing')),
      gpu: readGpu(get(values, 'resource.gpu')),
      network: {
        vpc: { name: String(get(network, 'vpc.name') ?? '') },
        subnet: { name: String(get(network, 'subnet.name') ?? 'default') },
        securitygroup: get(network, 'securitygroup.name') ? { name: String(get(network, 'securitygroup.name')) } : undefined,
        Bandwidth: get(network, 'Bandwidth') as string | number | undefined,
        egressRate: get(network, 'egressRate') as string | number | undefined,
        bandwidthPricing: normalizeVendorDeviceMeta(readVendorDeviceMeta(values, 'resource.network.bandwidthPricing')),
        dnsConfig: {
          nameservers: flattenDnsNameservers(dnsConfig.nameservers),
          searches: flattenDnsSearches(dnsConfig.searches),
          options: Array.isArray(dnsConfig.options) ? (dnsConfig.options as { name: string; value: string }[]) : undefined,
        },
        services: Array.isArray(services) ? services : undefined,
        nics,
      },
    },
    volumes: enrichVolumesFromForm(values, normalizeContainerVolumeList(values.volumes)),
    ...(optionMeta && typeof optionMeta === 'object' && !Array.isArray(optionMeta)
      ? { optionMeta: optionMeta as Record<string, unknown> }
      : {}),
  };
  const registry = readRegistryPayloadFromValues({
    ...values,
    registry: values.registry ?? get(values, 'registry'),
    'registry.server': get(values, 'registry.server') ?? values['registry.server'],
    'registry.username': get(values, 'registry.username') ?? values['registry.username'],
    'registry.password': get(values, 'registry.password') ?? values['registry.password'],
  });
  if (registry) {
    spec.system = { ...(spec.system || {}), registry };
  }

  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, values);
  return filterCiSpecByEnabledModules(spec, enabledModules, ctx.sku?.skuType);
}

export function ciToForm(spec: CiSpec, _ctx?: PipelineContext): Record<string, unknown> {
  const network = spec.resource.network;
  const services = deserializeServiceListFromApi((network.services ?? network.service) as unknown[] | undefined);
  const memoryGi = normalizeMemoryForForm(spec.resource.memory);
  const cpuRequest = readRequirementFromSpecCpu(spec.resource.cpu);
  const gpu = normalizeGpuFromApi(spec.resource.gpu) ?? spec.resource.gpu;
  const vpcName = readFormNetworkName(typeof network.vpc === 'string' ? network.vpc : network.vpc?.name);

  return {
    name: spec.name,
    description: spec.description,
    runStrategy: spec.runStrategy ?? 'Always',
    workspace: spec.workspace,
    namespace: spec.namespace,
    datacenter: spec.datacenter,
    systemDiskSize: spec.systemDiskSize,
    ...(spec.systemDiskPricing ? { systemDiskPricing: spec.systemDiskPricing } : {}),
    basic: {
      image: {
        key: spec.system?.imageID,
        url: spec.system?.url ?? spec.system?.image,
        image: spec.system?.image ?? spec.system?.url,
        name: spec.system?.name,
        imageName: spec.system?.name,
        architecture: spec.system?.architecture,
        os: spec.system?.os,
        version: spec.system?.version,
        command: spec.system?.command?.join(','),
        args: spec.system?.args?.join(','),
        env: spec.system?.env,
      },
    },
    system: {
      ...spec.system,
      architecture: spec.system?.architecture,
      ...(spec.system?.options ? { options: spec.system.options } : {}),
    },
    architecture: spec.system?.architecture,
    network: { nics: network.nics },
    resource: {
      cpu: cpuRequest != null ? { request: cpuRequest, limit: cpuRequest, pricing: spec.resource.cpu?.pricing } : spec.resource.cpu,
      memory: memoryGi ?? spec.resource.memory,
      memoryPricing: spec.resource.memoryPricing,
      cpuPricing: spec.resource.cpuPricing,
      gpuPricing: spec.resource.gpuPricing,
      gpu,
      network: {
        vpc: vpcName ? { name: vpcName } : {},
        subnet: typeof network.subnet === 'string' ? { name: network.subnet } : network.subnet,
        securitygroup: readFormNetworkRef(network.securitygroup),
        Bandwidth: network.Bandwidth ?? network.egressRate,
        egressRate: network.egressRate ?? network.Bandwidth,
        bandwidthPricing: network.bandwidthPricing,
        services,
        dnsConfig: {
          nameservers: toFormDnsNameservers(network.dnsConfig?.nameservers),
          searches: toFormDnsSearches(network.dnsConfig?.searches),
          options: network.dnsConfig?.options,
        },
      },
    },
    volumes: spec.volumes ?? [],
    ...(spec.optionMeta ? { optionMeta: spec.optionMeta } : {}),
    ...(spec.system?.registry ? { registry: spec.system.registry } : {}),
  };
}

function readRequirementFromSpecCpu(cpu: CiSpec['resource']['cpu']): number | undefined {
  return readConfiguredCpuFromRecord(cpu);
}

export function ciGetInitialFormValues(ctx: PipelineContext): Record<string, unknown> {
  return {
    runStrategy: 'Always',
    resource: {
      network: {
        subnet: { name: 'default' },
        services: DEFAULT_CONTAINER_SERVICES,
      },
    },
    /** 系统盘不预填，由用户通过「系统盘配置」添加入口自行配置 */
    volumes: [],
    skuResourceModules: buildDefaultDirectResourceModules('ContainerInstance'),
    ...(ctx.cluster ? { datacenter: ctx.cluster } : {}),
  };
}
